import { purchaseOrderDetail } from '../../model/purchase-order';

/**
 * Lines the auto-fill could not put a quantity on.
 *
 * "ดึงสินค้าใกล้หมด" used to guess when "จำนวนสั่งซื้อเริ่มต้น" was not set:
 * twice the order point minus stock, or ten. With a high order point and no
 * cap that guess ran away (order point 120, 100 in stock -> 140 ordered).
 * The shop's decision, 2026-09-09: do not guess. Pull the product in, leave
 * the quantity blank, put it at the TOP of the table so it cannot be missed,
 * and refuse to save or send until someone has typed a number.
 *
 * Blank lines sit above everything else in the order they arrived -- the
 * first one pulled or keyed is the first one down. A line keeps its place
 * once a quantity is typed; rows do not jump under the cursor.
 */

/**
 * `needs_quantity` marks a line the auto-fill pulled in without a default;
 * it stays on the line for good, so the cell may go back to blank when the
 * number is cleared instead of silently becoming 1 the way an ordinary
 * line does. A line is UNFINISHED while it carries that mark and has no
 * usable quantity.
 */
export const needsQuantity = (line: purchaseOrderDetail): boolean =>
    !!line.needs_quantity && !(Number(line.quantity) >= 1);

export const linesNeedingQuantity = (detail: purchaseOrderDetail[] | undefined): purchaseOrderDetail[] =>
    (detail || []).filter(needsQuantity);

/**
 * Inserts a blank line after the last blank line already at the top, so
 * blanks read top-down in arrival order and everything else stays below.
 */
export const insertBlankLine = (detail: purchaseOrderDetail[], line: purchaseOrderDetail): purchaseOrderDetail[] => {
    const next = [...detail];
    let at = 0;
    while (at < next.length && needsQuantity(next[at])) at++;
    next.splice(at, 0, { ...line, quantity: 0, total_cost: 0, needs_quantity: true });
    return next.map((d, i) => ({ ...d, key: i + 1 }));
};

export const describeBlankLines = (lines: purchaseOrderDetail[]): string => {
    const names = lines.map((l) => l.product_name);
    return names.slice(0, 3).join(', ') + (names.length > 3 ? ` และอีก ${names.length - 3} รายการ` : '');
};
